import { Button, Card, CircularProgress, FormControlLabel, Stack, Switch, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { profileApi } from '../../../../api/profile'
import { extractApiErrorMessage } from '../../../../utils/api'
import { SettingsSectionShell } from './SettingsSectionShell'

export const PrivacySection = () => {
  const { data: profile } = profileApi.useGetProfileQuery()
  const [updateUserProfile, updateState] = profileApi.useUpdateUserProfileMutation()
  const [showProjects, setShowProjects] = useState(true)
  const [showTeams, setShowTeams] = useState(true)
  const [showSocialOrders, setShowSocialOrders] = useState(true)

  useEffect(() => {
    if (!profile?.id) return
    setShowProjects(profile.show_projects_participation !== false)
    setShowTeams(profile.show_teams_participation !== false)
    setShowSocialOrders(profile.show_social_orders_participation !== false)
  }, [
    profile?.id,
    profile?.show_projects_participation,
    profile?.show_teams_participation,
    profile?.show_social_orders_participation,
  ])

  const hasChanges = useMemo(() => {
    if (!profile?.id) return false
    return (
      showProjects !== (profile.show_projects_participation !== false) ||
      showTeams !== (profile.show_teams_participation !== false) ||
      showSocialOrders !== (profile.show_social_orders_participation !== false)
    )
  }, [profile?.id, profile?.show_projects_participation, profile?.show_teams_participation, profile?.show_social_orders_participation, showProjects, showTeams, showSocialOrders])

  const handleSave = async () => {
    if (!profile?.id) return

    try {
      await updateUserProfile({
        userId: profile.id,
        body: {
          show_projects_participation: showProjects,
          show_teams_participation: showTeams,
          show_social_orders_participation: showSocialOrders,
        },
      }).unwrap()
      toast.success('Настройки приватности сохранены')
    } catch (error) {
      toast.error(extractApiErrorMessage(error) ?? 'Не удалось сохранить настройки приватности')
    }
  }

  return (
    <SettingsSectionShell
      title="Приватность"
      description="Настройки приватности управляют тем, что увидят другие пользователи в вашем публичном профиле, но не меняют ваши реальные связи и участие."
    >
      <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
        <Stack gap={1}>
          <Typography variant="black-14-medium">Конфиденциальность</Typography>
          <Typography variant="black-14-light" color="text.secondary">
            Email нужен для входа и служебных писем — это не часть публичной визитки. Другие пользователи не видят
            ваш email в профиле.
          </Typography>
          <Typography variant="black-14-light" color="text.secondary">
            В публичном профиле видны имя, описание, участие и другие поля, которые вы заполняете как визитку.
          </Typography>
          <Typography variant="black-14-light" color="text.secondary">
            Командное пространство проекта (внутренние ссылки и карточки участников) доступно только автору и
            активным участникам этого проекта.
          </Typography>
        </Stack>
      </Card>

      <Card variant="primaryAdaptiveCard" sx={{ p: 2 }}>
        <Stack gap={1.5}>
          <Typography variant="black-14-medium">Видимость участия</Typography>
          <Typography variant="black-14-light" color="text.secondary">
            Эти настройки влияют на то, какие вкладки участия увидят другие пользователи в вашем публичном профиле.
            Свой профиль вы продолжаете видеть полностью.
          </Typography>
          {!profile?.id ? (
            <Stack direction="row" gap={1.5} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="black-14-light" color="text.secondary">
                Загружаем настройки...
              </Typography>
            </Stack>
          ) : (
            <>
              <FormControlLabel
                control={<Switch checked={showProjects} onChange={(e) => setShowProjects(e.target.checked)} />}
                label="Показывать участие в проектах"
              />
              <FormControlLabel
                control={<Switch checked={showTeams} onChange={(e) => setShowTeams(e.target.checked)} />}
                label="Показывать команды"
              />
              <FormControlLabel
                control={<Switch checked={showSocialOrders} onChange={(e) => setShowSocialOrders(e.target.checked)} />}
                label="Показывать соц-заказы"
              />
              <Stack direction="row">
                <Button variant="follow" onClick={() => void handleSave()} disabled={!hasChanges || updateState.isLoading}>
                  {updateState.isLoading ? 'Сохраняем...' : 'Сохранить'}
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </Card>
    </SettingsSectionShell>
  )
}
